import classnames from 'classnames';
import { useGeolocationManager } from 'contexts/GeolocationContext';
import propTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import { Breadcrumb } from '@/design-system/atoms/Breadcrumb';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Theme } from '@/design-system/atoms/Theme';
import { useSearchParams } from '@/hooks/useSearchParams';
import { trackInteraction } from '@/utils/analytics';
import { capitalizeWords } from '@/utils/stringFormat';

import { useLocationFilterContext } from '../../contexts/LocationFilterContext';
import { useLocationSearchContext } from '../../contexts/LocationSearchContext';
import { useLocationStateContext } from '../../contexts/LocationStateContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import styles from './LocationHero.module.scss';
import { LocationHeroDefault } from './LocationHeroDefault';
import { LocationHeroResults } from './LocationHeroResults';

/**
 * The location hero component leads the location search pages and has two states: default and results.
 */
const LocationHero = ({ breadcrumbs, hideMobileIllustration, ...props }) => {
    const searchParams = useSearchParams();

    const {
        coveo: {
            controller: { searchBox, resultList },
        },
        handleSubmit,
        setCoveoAnalytics,
    } = useLocationSearchContext();

    const { title } = useLocationUIContext();

    const { openPanel } = useLocationFilterContext();

    const state = useLocationStateContext();

    const [searchValue, setSearchValue] = useState(searchParams.get('term') ?? '');
    const [typeaheadItems, setTypeaheadItems] = useState([]);

    const { currentGeolocation, clearGeolocation } = useGeolocationManager();

    const [zipCityValue, setZipCityValue] = useState(
        searchParams.get('zip-code') ||
            searchParams.get('city') ||
            currentGeolocation?.recentSearch ||
            '',
    );

    const [isLoading, setIsLoading] = useState(false);

    const [placeSuggestions, setPlaceSuggestions] = useState([]);

    const [zipInvalid, setZipInvalid] = useState(false);

    useEffect(() => {
        return resultList.subscribe(() => {
            setIsLoading(resultList.state.isLoading);
        });
    }, [resultList]);

    useEffect(() => {
        return searchBox.subscribe(() => {
            const newTypeaheadItems = searchBox.state.suggestions.map((s, i) => ({
                id: i,
                text: capitalizeWords(s.rawValue),
                rawValue: s.rawValue,
            }));

            setTypeaheadItems(newTypeaheadItems);
        });
    }, [searchBox]);

    const handleSetSearchValue = useCallback(
        (value) => {
            if (value.length >= 2) {
                searchBox.updateText(value);
            } else {
                searchBox.clear();
            }

            setSearchValue(value);
        },
        [searchBox],
    );

    const handleSelectSearchValue = useCallback(
        (item) => {
            if (item === null) {
                searchBox.clear();
                return;
            }

            //analytics
            trackInteraction({
                componentName: 'Location Hero',
                componentTitle: title,
                eventName: 'search',
                interactionType: 'predictive_search_click',
                actionLabel: item.text,
                selector: 'search',
                searchTerm: searchValue,
            });

            handleSubmit({
                term: item.text,
                zipCityValue,
                analyticsCallback: () =>
                    setCoveoAnalytics(
                        'querySuggestionClick',
                        {
                            id: 'location-search-searchbox',
                            suggestion: item?.rawValue,
                        },
                        false,
                    ),
            });
        },
        [searchBox, handleSubmit, zipCityValue, searchValue, title, setCoveoAnalytics],
    );

    const handleSelectZipCityValue = useCallback(
        (item) => {
            if (item === null) {
                setCoveoAnalytics('searchboxSubmit', {}, false);
                return;
            }

            //analytics
            trackInteraction({
                componentName: 'Location Hero',
                componentTitle: title,
                eventName: 'search',
                interactionType: 'predictive_search_click',
                actionLabel: item.text,
                selector: 'search',
                searchTerm: item.text,
            });

            setZipCityValue(item.text);
            setZipInvalid(false);

            handleSubmit({ term: searchValue, zipCityValue: item.text });
        },
        [handleSubmit, searchValue, title, setCoveoAnalytics],
    );

    const handleZipCityInputChange = useCallback(
        (value, suggestions) => {
            setZipCityValue(value);
            setPlaceSuggestions(suggestions);
        },
        [setZipCityValue, setPlaceSuggestions],
    );

    const handleSubmitCallback = async ({ term, zipCityValue, viewMode }) => {
        if (!zipCityValue) {
            // if there is no zip/city value, reset geolocation recent search
            await clearGeolocation();
        }

        // set the default value for the zip city to ensure that they are in sync when submitting
        setZipCityValue(zipCityValue);
        //clear city suggestion
        setPlaceSuggestions([]);
        handleSubmit({ term, zipCityValue, viewMode });
    };

    const handleAutoSelect = useCallback(
        (e, value) => {
            const triggerEl = e?.relatedTarget;
            if (triggerEl?.getAttribute('data-submit-type') === 'location-search') {
                return;
            } else {
                if (value) {
                    setZipCityValue(value);
                    setZipInvalid(false);
                }
            }
        },
        [setZipCityValue],
    );

    return (
        <Theme name={Theme.NAME.LOCATIONS} {...props}>
            <ContentContainer
                className={classnames(styles[`location-hero`], styles[`location-hero--${state}`])}
            >
                {breadcrumbs && breadcrumbs.length > 0 && (
                    <Breadcrumb
                        className={styles[`location-hero__breadcrumb`]}
                        items={breadcrumbs}
                        {...(openPanel ? { inert: '' } : '')}
                        analytics={{ componentName: 'Location Hero', componentTitle: title }}
                    />
                )}
                {state === 'default' && (
                    <LocationHeroDefault
                        {...props}
                        inputNameTypeahead="term"
                        inputNameZipCity="zip-city"
                        searchValue={searchValue}
                        setSearchValue={handleSetSearchValue}
                        onSelectionItemChange={handleSelectSearchValue}
                        onSelectionZipCityChange={handleSelectZipCityValue}
                        typeaheadItems={typeaheadItems}
                        zipCityValue={zipCityValue}
                        setZipCityValue={setZipCityValue}
                        onSubmit={handleSubmitCallback}
                        submitDisabled={isLoading}
                        hideMobileIllustration={hideMobileIllustration}
                        placeSuggestions={placeSuggestions}
                        handleZipCityInputChange={handleZipCityInputChange}
                        handleAutoSelect={handleAutoSelect}
                        zipInvalid={zipInvalid}
                        setZipInvalid={setZipInvalid}
                    />
                )}
                {state === 'results' && (
                    <LocationHeroResults
                        {...props}
                        inputNameTypeahead="term"
                        inputNameZipCity="zip-city"
                        searchValue={searchValue}
                        setSearchValue={handleSetSearchValue}
                        onSelectionItemChange={handleSelectSearchValue}
                        onSelectionZipCityChange={handleSelectZipCityValue}
                        typeaheadItems={typeaheadItems}
                        zipCityValue={zipCityValue}
                        setZipCityValue={setZipCityValue}
                        onSubmit={handleSubmitCallback}
                        submitDisabled={isLoading}
                        hideMobileIllustration={hideMobileIllustration}
                        placeSuggestions={placeSuggestions}
                        handleZipCityInputChange={handleZipCityInputChange}
                        handleAutoSelect={handleAutoSelect}
                        zipInvalid={zipInvalid}
                        setZipInvalid={setZipInvalid}
                    />
                )}
            </ContentContainer>
        </Theme>
    );
};

LocationHero.propTypes = {
    /**
     * The state of the location hero component.
     */
    state: propTypes.oneOf(['default', 'results']),
    /**
     * The breadcrumbs to display.
     */
    breadcrumbs: Breadcrumb.propTypes.items,
};

export default LocationHero;
